import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Check, KeyRound, CalendarClock, Sparkles, Infinity as InfinityIcon, ArrowRight, Lock, UserPlus } from 'lucide-react';
import AiDialog from './AiDialog';
import AiUsageMeter from './AiUsageMeter';
import { PLAN_LABELS, formatResetDate, planComparison, planNumbers } from '../../lib/aiHosted';
import useAiAllowance from './useAiAllowance';

/** Pro perks with live numbers from /ai/allowance (contract defaults while loading). */
function proPerks(data) {
  const n = planNumbers(data);
  const credits = n.proCredits ? `${n.proCredits.toLocaleString()} AI credits every ${n.proPeriod}${n.multiplier ? `, ${n.multiplier} the Free plan` : ''}` : 'More AI credits every month';
  return [
    { icon: Sparkles, text: credits },
    { icon: Crown, text: n.proModel ? `${n.proModel}, our most accurate model for coding tasks` : 'Our most accurate model for coding tasks' },
    { icon: InfinityIcon, text: 'Use your own API key for unlimited AI' },
    { icon: Check, text: 'Unlimited code sets, CSV/PDF exports and FY update alerts' },
  ];
}

function PlanStrip({ current, data }) {
  return (
    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="AI credits by plan">
      {planComparison(data).map((p) => {
        const isCurrent = p.id === current;
        return (
          <li
            key={p.id}
            className={`rounded-2xl border p-2.5 text-xs ${isCurrent ? 'border-primary bg-sky-soft' : p.id === 'pro' ? 'border-pop/50 bg-pop-soft' : 'border-line/25 bg-surface-2'}`}
            aria-current={isCurrent ? 'true' : undefined}
          >
            <p className="flex items-center gap-1 font-display text-sm font-bold text-ink">
              {p.label}
              {isCurrent && <span className="rounded-full bg-primary px-1.5 py-px text-[10px] font-bold uppercase text-primary-ink">You</span>}
            </p>
            <p className="mt-0.5 leading-snug text-muted">{p.credits}</p>
            {p.byok && <p className="mt-1 font-semibold text-ink">+ own key, unlimited</p>}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * 402 AI_LIMIT_REACHED. Shows the meter, reset time, what Pro adds, and for Pro/Team the own-key route.
 * `onUseOwnKey` (Pro with a key configured) switches mode and closes.
 */
export function AiLimitDialog({ open, onClose, details = {}, credits, plan, signedIn, hasOwnKey, onUseOwnKey }) {
  const { data } = useAiAllowance();
  const perks = proPerks(data);
  const paid = plan === 'pro' || plan === 'team';
  const period = credits && credits.known ? credits.period : plan === 'guest' ? 'day' : 'month';
  const resetAt = details.resetAt || (credits && credits.resetAt);
  const resetText = formatResetDate(resetAt);
  const title =
    plan === 'guest'
      ? "You've used today's free AI credits"
      : period === 'day'
        ? "You've reached today's AI limit"
        : paid
          ? "You've used this month's included AI credits"
          : "You've used this month's AI credits";

  return (
    <AiDialog open={open} onClose={onClose} labelledBy="ai-limit-title" describedBy="ai-limit-desc" size="lg">
      <div className="bg-navy px-5 pb-5 pt-6 text-on-navy sm:px-7 sm:pt-7">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-pop px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-navy">
          <Crown className="h-3.5 w-3.5" aria-hidden="true" /> {PLAN_LABELS[plan] || 'Your'} plan limit
        </span>
        <h2 id="ai-limit-title" className="mt-3 pr-8 font-display text-2xl font-extrabold leading-tight text-on-navy sm:text-3xl">
          {title}
        </h2>
        <p id="ai-limit-desc" className="mt-2 flex flex-wrap items-center gap-1.5 text-sm text-on-navy/80">
          <CalendarClock className="h-4 w-4 text-sun" aria-hidden="true" />
          {resetText ? (
            <>
              Your credits reset on <strong className="text-on-navy">{resetText}</strong>.
            </>
          ) : (
            <>Your credits reset at the start of the next {period}.</>
          )}{' '}
          Nothing you've done is lost.
        </p>
      </div>

      <div className="space-y-5 px-5 py-5 sm:px-7 sm:py-6">
        {credits && credits.known && <AiUsageMeter credits={credits} />}

        {paid ? (
          <div className="rounded-2xl border border-primary/30 bg-sky-soft p-4">
            <p className="flex items-center gap-2 font-display text-base font-bold text-ink">
              <KeyRound className="h-5 w-5 text-primary" aria-hidden="true" /> Keep going with your own key
            </p>
            <p className="mt-1 text-sm text-ink/80">
              On {PLAN_LABELS[plan]}, AI runs unlimited on your own OpenAI, Anthropic, Gemini or OpenRouter key. You pay your provider directly; no credits are used.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {hasOwnKey ? (
                <button type="button" className="btn-primary btn-sm" onClick={onUseOwnKey}>
                  <KeyRound className="h-4 w-4" aria-hidden="true" /> Switch to my own key
                </button>
              ) : (
                <Link to="/settings#ai-mode" className="btn-primary btn-sm" onClick={onClose}>
                  <KeyRound className="h-4 w-4" aria-hidden="true" /> Add my own key
                </Link>
              )}
              {plan === 'pro' && (
                <Link to="/pricing" className="btn-outline btn-sm" onClick={onClose}>
                  See the Team plan
                </Link>
              )}
            </div>
          </div>
        ) : (
          <div>
            <h3 className="font-display text-base font-bold text-ink">Pro gives you</h3>
            <ul className="mt-2 grid gap-2 sm:grid-cols-2">
              {perks.map((p) => (
                <li key={p.text} className="flex items-start gap-2.5 rounded-xl bg-surface-2 p-2.5 text-sm text-ink">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-pop text-navy">
                    <p.icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="pt-0.5">{p.text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <PlanStrip current={plan} data={data} />

        <div className="flex flex-col-reverse gap-2 border-t border-line/15 pt-4 sm:flex-row sm:items-center sm:justify-end">
          <button type="button" className="btn-ghost" onClick={onClose}>
            {paid ? 'Close' : 'Maybe later'}
          </button>
          {!signedIn && (
            <Link to="/register" className="btn-outline" onClick={onClose}>
              <UserPlus className="h-4 w-4" aria-hidden="true" /> Create a free account
            </Link>
          )}
          {!paid && (
            <Link to="/pricing" className="btn-pop" onClick={onClose}>
              <Crown className="h-4 w-4" aria-hidden="true" /> Upgrade to Pro <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>
    </AiDialog>
  );
}

/** 402 BYOK_REQUIRES_PRO: own keys are a Pro feature. Offer hosted (and retry) or upgrade. */
export function AiByokProDialog({ open, onClose, onUseHosted, signedIn }) {
  return (
    <AiDialog open={open} onClose={onClose} labelledBy="ai-byok-title" describedBy="ai-byok-desc" size="sm">
      <div className="px-5 pb-5 pt-6 sm:px-6">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-pop text-navy">
          <Lock className="h-6 w-6" aria-hidden="true" />
        </span>
        <h2 id="ai-byok-title" className="mt-4 pr-8 font-display text-xl font-extrabold text-ink">
          Your own API key is a Pro feature
        </h2>
        <p id="ai-byok-desc" className="mt-1.5 text-sm text-muted">
          Your plan includes ICD Code Finder AI, so you can keep working without a key. Switch back and we'll run this request now, or upgrade to use your own key with no limits.
        </p>
        <div className="mt-5 flex flex-col gap-2">
          <button type="button" className="btn-primary w-full justify-center" onClick={onUseHosted}>
            <Sparkles className="h-4 w-4" aria-hidden="true" /> Switch to included AI
          </button>
          <Link to={signedIn ? '/pricing' : '/register'} className="btn-pop w-full justify-center" onClick={onClose}>
            <Crown className="h-4 w-4" aria-hidden="true" /> {signedIn ? 'Upgrade to Pro' : 'Create an account'}
          </Link>
          <button type="button" className="btn-ghost w-full justify-center" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </AiDialog>
  );
}
